import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Table,
  Typography,
  Space,
  Input,
  InputNumber,
  Tag,
  Button,
  notification,
  Grid,
  Row,
  Col,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { PlusOutlined, DeleteOutlined, SaveOutlined, ControlOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { CustomButtonComponent } from "../../global/CustomButtonComponent";
import { useFetchCalculationSettings } from "../../../hooks/calculationSettings/useFetchCalculationSettings";
import { useCreateCalculationSetting } from "../../../hooks/calculationSettings/useCreateCalculationSetting";
import { useUpdateCalculationSetting } from "../../../hooks/calculationSettings/useUpdateCalculationSetting";
import { CALCULATION_SETTING_KEY_CATALOG } from "../../../helpers/constants/calculationSettingKeyCatalog";

const { Text, Title } = Typography;

type EditableRow = {
  rowKey: string;
  calculation_setting_id?: number;
  setting_key: string;
  setting_value: number | null;
  description?: string;
  status: "saved" | "dirty" | "new";
};

type CalculationSettingsListModalComponentProps = {
  open: boolean;
  providerId?: number;
  providerName?: string;
  onCancel: () => void;
};

export const CalculationSettingsListModalComponent = ({
  open,
  providerId,
  providerName,
  onCancel,
}: CalculationSettingsListModalComponentProps) => {
  const screens = Grid.useBreakpoint();
  const [refreshTick, setRefreshTick] = useState(0);
  const [rows, setRows] = useState<EditableRow[]>([]);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [saving, setSaving] = useState(false);

  // Memoized so this object's reference only changes when providerId or
  // refreshTick actually change — useFetchCalculationSettings' effect keys
  // off [query] by reference, so a fresh literal here on every render would
  // refetch every render, trigger a state update, re-render, refetch... an
  // infinite loop that crashes the page.
  const settingsQuery = useMemo(
    () => ({ provider_id: providerId, page: 1, limit: 50 }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [providerId, refreshTick]
  );
  const settingsList = useFetchCalculationSettings(settingsQuery);
  const createCalculationSettingData = useCreateCalculationSetting;
  const updateCalculationSettingData = useUpdateCalculationSetting;

  // useFetchCalculationSettings stores the whole axios response as state
  // (its dataType return type doesn't reflect this), so the rows array is
  // actually at settingsList.data.data (the API body's own `data` field),
  // not settingsList.data. Memoized so this array reference is stable
  // across renders that don't change the underlying fetch result.
  const settingsRows: any[] = useMemo(
    () => (settingsList as any)?.data?.data ?? [],
    [settingsList]
  );

  // Re-seed local editable rows whenever the fetched list changes (initial
  // load, provider switch, or after a successful save triggers a refetch).
  useEffect(() => {
    const fetched: EditableRow[] = settingsRows.map((item: any) => ({
      rowKey: String(item.calculation_setting_id),
      calculation_setting_id: item.calculation_setting_id,
      setting_key: item.setting_key,
      setting_value: item.setting_value,
      description: item.description,
      status: "saved",
    }));
    setRows(fetched);
    setIsAddingNew(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsRows, refreshTick]);

  const hasPendingChanges = rows.some((r) => r.status === "dirty" || r.status === "new");
  const usedKeys = new Set(rows.map((r) => r.setting_key.trim().toLowerCase()).filter(Boolean));

  const handleStartAddNew = () => {
    setRows((prev) => [
      ...prev,
      {
        rowKey: `new-${Date.now()}`,
        setting_key: "",
        setting_value: null,
        description: "",
        status: "new",
      },
    ]);
    setIsAddingNew(true);
  };

  const handleCancelAddNew = (rowKey: string) => {
    setRows((prev) => prev.filter((r) => r.rowKey !== rowKey));
    setIsAddingNew(false);
  };

  const handleFieldChange = (rowKey: string, field: keyof EditableRow, value: any) => {
    setRows((prev) =>
      prev.map((r) =>
        r.rowKey === rowKey
          ? { ...r, [field]: value, status: r.status === "saved" ? "dirty" : r.status }
          : r
      )
    );
  };

  const handlePickCatalogKey = (key: string) => {
    setRows((prev) => {
      // Reuse the still-blank starter row (opened by "+ Click to add a new
      // calculation setting") for the first pick. Every pick after that
      // appends its own new row instead of overwriting the one before it.
      const blankNewIndex = prev.findIndex((r) => r.status === "new" && !r.setting_key.trim());

      if (blankNewIndex !== -1) {
        const updated = [...prev];
        updated[blankNewIndex] = { ...updated[blankNewIndex], setting_key: key };
        return updated;
      }

      const catalogEntry = CALCULATION_SETTING_KEY_CATALOG.find((entry) => entry.key === key);

      return [
        ...prev,
        {
          rowKey: `new-${Date.now()}`,
          setting_key: key,
          setting_value: null,
          description: catalogEntry?.description ?? "",
          status: "new",
        },
      ];
    });
  };

  const handleSaveChanges = async () => {
    const invalidNewRow = rows.find(
      (r) => r.status === "new" && (!r.setting_key.trim() || r.setting_value === null || r.setting_value === undefined)
    );

    if (invalidNewRow) {
      notification.error({
        message: "Setting key and value are both required before saving.",
      });
      return;
    }

    setSaving(true);
    try {
      const results = await Promise.all(
        rows
          .filter((r) => r.status === "dirty" || r.status === "new")
          .map((r) =>
            r.status === "new"
              ? createCalculationSettingData({
                  provider_id: providerId,
                  setting_key: r.setting_key.trim(),
                  setting_value: r.setting_value,
                  description: r.description,
                })
              : updateCalculationSettingData(r.calculation_setting_id!, {
                  setting_value: r.setting_value,
                  description: r.description,
                })
          )
      );

      if (results.every(Boolean)) {
        setRefreshTick((tick) => tick + 1);
      }
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error.message || "An unexpected error occurred.",
      });
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<EditableRow> = [
    {
      title: "SETTING KEY",
      dataIndex: "setting_key",
      key: "setting_key",
      width: 240,
      render: (_, record) =>
        record.status === "new" ? (
          <Input
            value={record.setting_key}
            placeholder="Pick from the list, or type your own →"
            onChange={(e) => handleFieldChange(record.rowKey, "setting_key", e.target.value)}
          />
        ) : (
          <Text strong style={{ color: "#1e293b" }}>{record.setting_key}</Text>
        ),
    },
    {
      title: "VALUE",
      dataIndex: "setting_value",
      key: "setting_value",
      width: 220,
      render: (_, record) => (
        <InputNumber
          style={{ width: "100%" }}
          value={record.setting_value as any}
          placeholder="e.g. 6500 or 0.15"
          step={0.0001}
          onChange={(value) => handleFieldChange(record.rowKey, "setting_value", value)}
        />
      ),
    },
    {
      title: "DESCRIPTION",
      dataIndex: "description",
      key: "description",
      render: (_, record) => (
        <Input.TextArea
          value={record.description}
          placeholder="What this setting controls"
          rows={2}
          onChange={(e) => handleFieldChange(record.rowKey, "description", e.target.value)}
        />
      ),
    },
    {
      title: "",
      key: "actions",
      width: 50,
      render: (_, record) =>
        record.status === "new" ? (
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleCancelAddNew(record.rowKey)}
            style={{ height: 30, width: 30, padding: 0 }}
          />
        ) : null,
    },
  ];

  const customHeader = (
    <div style={{
      background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
      padding: '24px',
      margin: '-20px -24px 24px -24px',
      borderRadius: '8px 8px 0 0',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '16px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <div style={{
          background: 'rgba(255,255,255,0.15)',
          padding: '12px',
          borderRadius: '12px',
          display: 'flex',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          backdropFilter: 'blur(4px)'
        }}>
          <ControlOutlined style={{ fontSize: '28px', color: '#fff' }} />
        </div>
        <div>
          <div style={{ fontSize: '20px', fontWeight: 600, letterSpacing: '-0.3px' }}>
            Calculation Settings
          </div>
          <div style={{ fontSize: '13px', opacity: 0.85, marginTop: '2px' }}>
            {providerName || "Provider"} — fee constants, tier add-ons, insurance rates
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      title={customHeader}
      closable
      open={open}
      onCancel={onCancel}
      footer={null}
      centered
      width={screens.xs ? "100%" : isAddingNew ? 1180 : 760}
      style={screens.xs ? { top: 0, padding: 0 } : { paddingBottom: 0 }}
      bodyStyle={{
        overflowY: "auto",
        overflowX: "hidden",
        maxHeight: screens.xs ? "calc(100vh - 120px)" : "calc(100vh - 220px)",
      }}
    >
      <div style={{ textAlign: "right", marginBottom: 16 }}>
        <CustomButtonComponent
          variant="primary"
          icon={<SaveOutlined />}
          onClick={handleSaveChanges}
          disabled={!hasPendingChanges}
          loading={saving}
        >
          Save Changes
        </CustomButtonComponent>
      </div>

      <Row gutter={16}>
        <Col span={isAddingNew && !screens.xs ? 15 : 24}>
          <Table
            columns={columns}
            dataSource={rows}
            rowKey="rowKey"
            pagination={false}
            scroll={{ y: screens.xs ? undefined : "max(160px, calc(100vh - 400px))" }}
            locale={{ emptyText: providerId ? "No calculation settings configured for this provider yet." : "Select a provider first." }}
          />

          {!isAddingNew && providerId && (
            <div
              onClick={handleStartAddNew}
              style={{
                marginTop: 12,
                border: "1px dashed #c9cdd8",
                borderRadius: 8,
                padding: "14px 16px",
                textAlign: "center",
                cursor: "pointer",
                color: "#334155",
                background: "#fafbfc",
              }}
            >
              <PlusOutlined /> <Text style={{ color: "#334155" }}>Click to add a new calculation setting</Text>
            </div>
          )}
        </Col>

        {isAddingNew && (
          <Col span={screens.xs ? 24 : 9}>
            <div style={{
              border: "1px solid #d6dee8",
              borderRadius: 12,
              padding: 16,
              background: "#fafbfc",
              boxSizing: "border-box",
              maxHeight: "max(240px, calc(100vh - 290px))",
              overflowY: "auto",
            }}>
              <Title level={5} style={{ marginTop: 0, color: "#1e293b" }}>Known Setting Keys</Title>

              <div style={{
                border: "1px solid #eef0f2",
                borderRadius: 8,
                padding: "10px 12px",
                marginBottom: 12,
                background: "#ffffff",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                  <InfoCircleOutlined style={{ color: "#334155" }} />
                  <Text strong style={{ fontSize: 12, color: "#1e293b" }}>Guidelines</Text>
                </div>
                <ul style={{ fontSize: 12, margin: 0, paddingLeft: 16, color: "#475569" }}>
                  <li>Click to add a key.</li>
                  <li>Greyed-out = already added.</li>
                  <li>Not listed? Type the exact key name.</li>
                </ul>
              </div>

              <Space direction="vertical" style={{ width: "100%" }} size={8}>
                {CALCULATION_SETTING_KEY_CATALOG.map((entry) => {
                  const isUsed = usedKeys.has(entry.key.toLowerCase());
                  return (
                    <div
                      key={entry.key}
                      onClick={() => !isUsed && handlePickCatalogKey(entry.key)}
                      style={{
                        padding: "8px 10px",
                        borderRadius: 8,
                        border: "1px solid #eef0f2",
                        background: isUsed ? "#d6dee8" : "#ffffff",
                        cursor: isUsed ? "not-allowed" : "pointer",
                        opacity: isUsed ? 0.5 : 1,
                      }}
                    >
                      <Tag color={isUsed ? "default" : "blue"} style={{ marginBottom: 4 }}>
                        {entry.key}
                      </Tag>
                      <div style={{ fontSize: 11, color: "#64748b" }}>{entry.description}</div>
                    </div>
                  );
                })}
              </Space>
            </div>
          </Col>
        )}
      </Row>
    </Modal>
  );
};
